/**
 * Os controles do documento original no painel da mesa única (E201.F2, GUS-47): a página (no PDF) e
 * o zoom. A página é da mesa, porque "Ver na fonte" também a escolhe; o zoom é do painel.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SourceDocumentRecordData } from '@/lib/case_source_documents';

import { ehPdf } from './useDocumentoOriginal';

const t = ptBR.mesaUnica.documento;

const ZOOM_MINIMO = 50;
const ZOOM_MAXIMO = 300;
const PASSO_DO_ZOOM = 25;

export interface DocumentoAberto {
  /** `null`: o primeiro da lista (a CCB). */
  documentoId: string | null;
  pagina: number;
}

function textoDaPagina(pagina: number, total: number | null): string {
  return total === null
    ? t.paginaSemTotal.replace('{n}', String(pagina))
    : t.pagina.replace('{n}', String(pagina)).replace('{total}', String(total));
}

export function ControlesDoDocumento({
  doc,
  aberto,
  zoom,
  onMudar,
  onZoom,
}: {
  doc: SourceDocumentRecordData;
  aberto: DocumentoAberto;
  zoom: number;
  onMudar: (aberto: DocumentoAberto) => void;
  onZoom: (zoom: number) => void;
}): JSX.Element {
  const total = ehPdf(doc) ? doc.page_count : 1;
  const ir = (pagina: number): void => onMudar({ documentoId: doc.id, pagina });
  return (
    <div className="flex flex-wrap items-center gap-sc-2">
      {ehPdf(doc) ? (
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={aberto.pagina <= 1}
            onClick={() => ir(aberto.pagina - 1)}
            data-testid="painel-documento-anterior"
          >
            {t.anterior}
          </Button>
          <span className="text-body-sm text-fg-muted" data-testid="painel-documento-pagina">
            {textoDaPagina(aberto.pagina, total)}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={total !== null && aberto.pagina >= total}
            onClick={() => ir(aberto.pagina + 1)}
            data-testid="painel-documento-proxima"
          >
            {t.proxima}
          </Button>
        </>
      ) : null}
      <span className="ml-auto flex items-center gap-sc-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={t.diminuir}
          disabled={zoom <= ZOOM_MINIMO}
          onClick={() => onZoom(zoom - PASSO_DO_ZOOM)}
          data-testid="painel-documento-menos"
        >
          −
        </Button>
        <span className="text-body-sm text-fg-muted" data-testid="painel-documento-zoom">
          {t.zoom.replace('{zoom}', String(zoom))}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={t.aumentar}
          disabled={zoom >= ZOOM_MAXIMO}
          onClick={() => onZoom(zoom + PASSO_DO_ZOOM)}
          data-testid="painel-documento-mais"
        >
          +
        </Button>
      </span>
    </div>
  );
}
